import { ObservableForm } from '@99designs/brief';
import { DesignerSource } from '@99designs/graph-utils/types';
import { linkTo } from '@99designs/routing';

interface Brief {
    id: string;
    designersWithSource: {
        source: DesignerSource;
    }[];
}

export interface BriefStep {
    title: string;
    stepHeading?: React.ReactNode;
    isAuthRequired?: boolean;
    nextActionLabel?: string | React.ReactNode;
    stepType?: StepType;

    // Fields shown on this step – used for filtering down validation errors
    fields?: string[];
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    render?: (form: ObservableForm<any>) => React.ReactNode;
}

export enum StepType {
    Designer,
    OrderPayment,
}

interface Options {
    steps: BriefStep[];
    currentStep: number;
    brief: Brief;
}

export class StepState {
    steps: BriefStep[];
    currentStepNumber: number;
    brief: Brief;

    constructor({ steps, currentStep, brief }: Options) {
        // Remove the find designers step if all our designers are direct
        if (
            brief.designersWithSource &&
            brief.designersWithSource.every((d) => d.source === 'DIRECT') &&
            brief.designersWithSource.length > 0
        ) {
            steps = steps.filter((step) => step.stepType !== StepType.Designer);
        }

        this.steps = steps;
        this.brief = brief;
        this.currentStepNumber = currentStep;
    }

    get currentStepIndex() {
        return this.currentStepNumber - 1;
    }

    currentStep(): BriefStep {
        const currentStep = this.steps[this.currentStepIndex];
        if (currentStep) {
            return currentStep;
        } else {
            throw new Error('Current step does not exist');
        }
    }

    hasStepAfter() {
        return !!this.steps[this.currentStepIndex + 1];
    }

    hasStepBefore() {
        return !!this.steps[this.currentStepIndex - 1];
    }

    nextStep() {
        const nextStep = this.steps[this.currentStepIndex + 1];
        return nextStep || this.currentStep();
    }

    prevStep() {
        const prevStep = this.steps[this.currentStepIndex - 1];
        return prevStep || this.currentStep();
    }

    url() {
        return linkTo.brief(this.brief.id, this.currentStepNumber);
    }

    nextUrl() {
        const step = this.hasStepAfter() ? this.currentStepNumber + 1 : this.currentStepNumber;
        return linkTo.brief(this.brief.id, step);
    }

    prevUrl() {
        const step = this.hasStepBefore() ? this.currentStepNumber - 1 : this.currentStepNumber;
        return linkTo.brief(this.brief.id, step);
    }

    percentageComplete() {
        return (this.currentStepNumber / this.steps.length) * 100;
    }

    stepCount() {
        return this.steps.length;
    }

    nextRequiresAuth() {
        return this.nextStep().isAuthRequired;
    }

    isDesignerStep() {
        return this.currentStep().stepType === StepType.Designer;
    }

    isInvoiceStep() {
        return this.currentStep().stepType === StepType.OrderPayment;
    }

    nextActionLabel() {
        const nextActionLabel = this.currentStep().nextActionLabel;
        if (nextActionLabel) {
            return nextActionLabel;
        }

        if (!this.hasStepAfter()) {
            return __("Let's do this!");
        } else {
            return __('Next');
        }
    }
}
